import { useState } from "react";
import { useNcm } from "@/features/fiscal/hooks/useFiscal";
import { PaginacaoRodape } from "@/components/PaginacaoRodape";
import { type TamanhoPagina } from "@/hooks/usePaginacaoLocal";
import type { NcmDto, SalvarNcmRequest, ImportarNcmResultado } from "@/features/fiscal/types";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Search, Hash, Plus, Pencil, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import { AvisoTabelaNacional } from "@/components/AvisoTabelaNacional";

// ── Formulário vazio ───────────────────────────────────────────────────────

const today = new Date().toISOString().substring(0, 10);

const EMPTY: SalvarNcmRequest = {
  codigo:       "",
  descricao:    "",
  isAtivo:      true,
  isMonofasico: false,
  usaCest:      false,
  dataInicio:   today,
  dataFim:      "9999-12-31",
};

// ── Página ────────────────────────────────────────────────────────────────

export default function NcmPage() {
  const [busca, setBusca]           = useState("");
  const [apenasAtivos, setApenasAtivos] = useState(false);
  const [pagina, setPagina]         = useState(1);
  const [tamanhoPagina, setTamanhoPagina] = useState<TamanhoPagina>(20);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteId, setDeleteId]     = useState<string | null>(null);
  const [editCodigo, setEditCodigo] = useState<string | null>(null);
  const [form, setForm]             = useState<SalvarNcmRequest>(EMPTY);
  const [saving, setSaving]         = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importing, setImporting]   = useState(false);
  const [importResult, setImportResult] = useState<ImportarNcmResultado | null>(null);

  const { user } = useAuth();
  const podeEditar = user?.isAdminGlobal ?? false;

  const ativo = apenasAtivos ? true : null;
  const { query, obterPorCodigo, criar, atualizar, excluir, importar } = useNcm(busca || undefined, ativo, pagina, tamanhoPagina);
  const itens = query.data?.itens ?? [];

  const totalItens = query.data?.total ?? 0;
  const primeiroItem = totalItens === 0 ? 0 : (pagina - 1) * tamanhoPagina + 1;
  const ultimoItem = Math.min(pagina * tamanhoPagina, totalItens);

  const set = (field: keyof SalvarNcmRequest, value: unknown) =>
    setForm(f => ({ ...f, [field]: value }));

  const openNew = () => { setEditCodigo(null); setForm(EMPTY); setDialogOpen(true); };

  const openEdit = async (codigo: string) => {
    try {
      const dto: NcmDto = await obterPorCodigo(codigo);
      setEditCodigo(codigo);
      setForm({
        codigo:       dto.codigo,
        descricao:    dto.descricao,
        isAtivo:      dto.isAtivo,
        isMonofasico: dto.isMonofasico,
        usaCest:      dto.usaCest,
        dataInicio:   dto.dataInicio ? dto.dataInicio.substring(0, 10) : today,
        dataFim:      dto.dataFim    ? dto.dataFim.substring(0, 10)    : "9999-12-31",
      });
      setDialogOpen(true);
    } catch {
      toast.error("Erro ao carregar NCM.");
    }
  };

  const handleSave = async () => {
    if (!form.codigo?.trim()) { toast.error("Código NCM é obrigatório."); return; }
    if (!form.descricao?.trim()) { toast.error("Descrição é obrigatória."); return; }
    setSaving(true);
    try {
      if (editCodigo) {
        await atualizar.mutateAsync({ codigo: editCodigo, req: form });
        toast.success("NCM atualizado.");
      } else {
        await criar.mutateAsync(form);
        toast.success("NCM criado.");
      }
      setDialogOpen(false);
    } catch {
      toast.error("Erro ao salvar NCM.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (deleteId === null) return;
    try {
      await excluir.mutateAsync(deleteId);
      toast.success("NCM excluído.");
    } catch {
      toast.error("Erro ao excluir NCM.");
    } finally {
      setDeleteId(null);
    }
  };

  const handleImport = async () => {
    if (!importFile) { toast.error("Selecione o arquivo JSON da tabela NCM."); return; }
    setImporting(true);
    setImportResult(null);
    try {
      const r = await importar.mutateAsync(importFile);
      setImportResult(r);
      toast.success(`Importação concluída: ${r.inseridos} novos, ${r.atualizados} atualizados.`);
    } catch {
      toast.error("Erro ao importar. Verifique se é o JSON oficial da Receita (campo 'Nomenclaturas').");
    } finally {
      setImporting(false);
    }
  };

  const fmtDate = (s: string | undefined) =>
    s ? new Date(s).toLocaleDateString("pt-BR") : "—";

  return (
    <div className="p-6 space-y-6">
      {/* Cabeçalho */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Hash className="h-6 w-6" /> NCM
          </h1>
          <p className="text-muted-foreground">Nomenclatura Comum do Mercosul</p>
        </div>
        {podeEditar && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => { setImportResult(null); setImportFile(null); setImportOpen(true); }}>
              <Upload className="h-4 w-4 mr-2" /> Importar JSON
            </Button>
            <Button onClick={openNew}><Plus className="h-4 w-4 mr-2" /> Novo NCM</Button>
          </div>
        )}
      </div>

      {!podeEditar && <AvisoTabelaNacional />}

      {/* Filtros */}
      <div className="flex items-center gap-4">
        <div className="relative max-w-sm flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por código ou descrição..."
            className="pl-9"
            value={busca}
            onChange={e => { setBusca(e.target.value); setPagina(1); }}
          />
        </div>
        <div className="flex items-center space-x-2">
          <Checkbox
            id="apenasAtivos"
            checked={apenasAtivos}
            onCheckedChange={v => { setApenasAtivos(!!v); setPagina(1); }}
          />
          <label htmlFor="apenasAtivos" className="text-sm cursor-pointer">Apenas Ativos</label>
        </div>
      </div>

      {/* Tabela */}
      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[140px]">Código NCM</TableHead>
              <TableHead>Descrição</TableHead>
              <TableHead className="text-center w-[80px]">Ativo</TableHead>
              <TableHead className="text-center w-[100px]">Monofásico</TableHead>
              <TableHead className="text-center w-[90px]">Usa CEST</TableHead>
              <TableHead className="w-[80px]" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {query.isLoading ? (
              <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">Carregando...</TableCell></TableRow>
            ) : itens.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">Nenhum NCM encontrado.</TableCell></TableRow>
            ) : itens.map(item => (
              <TableRow key={item.codigo}>
                <TableCell className="font-mono font-semibold">{item.codigo}</TableCell>
                <TableCell className="text-sm max-w-xs truncate">{item.descricao}</TableCell>
                <TableCell className="text-center">
                  <Badge variant={item.isAtivo ? "default" : "secondary"}>{item.isAtivo ? "Sim" : "Não"}</Badge>
                </TableCell>
                <TableCell className="text-center">
                  <Badge variant={item.isMonofasico ? "default" : "outline"}>{item.isMonofasico ? "Sim" : "Não"}</Badge>
                </TableCell>
                <TableCell className="text-center">
                  <Badge variant={item.usaCest ? "default" : "outline"}>{item.usaCest ? "Sim" : "Não"}</Badge>
                </TableCell>
                <TableCell>
                  {podeEditar && (
                    <div className="flex gap-1 justify-end">
                      <Button size="icon" variant="ghost" onClick={() => openEdit(item.codigo)}><Pencil className="h-4 w-4" /></Button>
                      <Button size="icon" variant="ghost" onClick={() => setDeleteId(item.codigo)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                    </div>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Paginação */}
      {query.data && (
        <PaginacaoRodape
          pagina={pagina}
          totalPaginas={query.data.totalPaginas}
          totalItens={totalItens}
          primeiroItem={primeiroItem}
          ultimoItem={ultimoItem}
          tamanhoPagina={tamanhoPagina}
          onPaginaChange={setPagina}
          onTamanhoPaginaChange={setTamanhoPagina}
        />
      )}

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{editCodigo ? "Editar NCM" : "Novo NCM"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            <div className="space-y-1">
              <Label>Código NCM <span className="text-destructive">*</span></Label>
              <Input
                value={form.codigo}
                onChange={e => set("codigo", e.target.value)}
                maxLength={20}
                placeholder="Ex.: 2204.10.00"
                readOnly={!!editCodigo}
                className={editCodigo ? "bg-muted" : ""}
              />
            </div>
            <div />
            <div className="col-span-2 space-y-1">
              <Label>Descrição <span className="text-destructive">*</span></Label>
              <Textarea
                value={form.descricao}
                onChange={e => set("descricao", e.target.value)}
                maxLength={1000}
                rows={3}
                placeholder="Descrição do NCM"
              />
            </div>
            <div className="space-y-1">
              <Label>Início de Vigência</Label>
              <Input
                type="date"
                value={form.dataInicio}
                onChange={e => set("dataInicio", e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label>Fim de Vigência</Label>
              <Input
                type="date"
                value={form.dataFim}
                onChange={e => set("dataFim", e.target.value)}
              />
            </div>
            <div className="col-span-2 flex flex-wrap gap-6">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="isAtivo"
                  checked={form.isAtivo}
                  onCheckedChange={v => set("isAtivo", !!v)}
                />
                <label htmlFor="isAtivo" className="text-sm cursor-pointer">Ativo</label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="isMonofasico"
                  checked={form.isMonofasico}
                  onCheckedChange={v => set("isMonofasico", !!v)}
                />
                <label htmlFor="isMonofasico" className="text-sm cursor-pointer">Monofásico</label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="usaCest"
                  checked={form.usaCest}
                  onCheckedChange={v => set("usaCest", !!v)}
                />
                <label htmlFor="usaCest" className="text-sm cursor-pointer">Usa CEST</label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleSave} disabled={saving}>{saving ? "Salvando..." : "Salvar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm excluir */}
      <AlertDialog open={deleteId !== null} onOpenChange={open => !open && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir NCM {deleteId}?</AlertDialogTitle>
            <AlertDialogDescription>Esta ação não pode ser desfeita.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive hover:bg-destructive/90">Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Importar JSON oficial */}
      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Importar tabela NCM (JSON oficial)</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2 text-sm">
            <p className="text-muted-foreground">
              Selecione o arquivo JSON da <b>Tabela NCM Vigente</b> (Receita/Siscomex), que contém o campo{" "}
              <code className="text-xs">Nomenclaturas</code>. Códigos existentes são atualizados; novos são inseridos.
            </p>
            <Input
              type="file"
              accept=".json,application/json"
              onChange={e => { setImportFile(e.target.files?.[0] ?? null); setImportResult(null); }}
            />
            {importResult && (
              <div className="rounded-md border bg-muted/40 p-3 space-y-1">
                <div>✅ Inseridos: <b>{importResult.inseridos}</b></div>
                <div>♻️ Atualizados: <b>{importResult.atualizados}</b></div>
                {importResult.erros > 0 && <div className="text-destructive">⚠️ Erros: <b>{importResult.erros}</b></div>}
                <div className="text-muted-foreground">Total no arquivo: {importResult.total}</div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setImportOpen(false)}>Fechar</Button>
            <Button onClick={handleImport} disabled={importing || !importFile}>
              {importing ? "Importando..." : "Importar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
